import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useVideo } from '../../contexts/VideoContext';
import Step1PetName from './steps/Step1PetName';
import Step2Photo from './steps/Step2Photo';
import Step3Emotion from './steps/Step3Emotion';
import Step4Activities from './steps/Step4Activities';
import Step5Review from './steps/Step5Review';
import Step6Payment from './steps/Step6Payment';
import Step7Generate from './steps/Step7Generate';
import './VideoWizard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000';

const VideoWizard = () => {
  const { daycareId } = useParams();
  const [searchParams] = useSearchParams();
  const { videoData, updateVideoData } = useVideo();
  const [loading, setLoading] = useState(true);

  // Ensure daycareId is set in context (handles page refresh)
  useEffect(() => {
    const ensureDaycareId = async () => {
      // If daycareId is already in context, we're good
      if (videoData.daycareId) {
        setLoading(false);
        return;
      }

      // Use daycareId from URL, or default to 'test-daycare' for testing
      const effectiveDaycareId = daycareId || 'test-daycare';

      try {
        const response = await axios.get(
          `${API_URL}/api/daycare/${effectiveDaycareId}`
        );

        const daycareData = response.data;
        updateVideoData({
          daycareId: daycareData.daycareId,
          daycareName: daycareData.daycareName,
          logoUrl: daycareData.brandingConfig?.logoUrl || '',
          brandColor: daycareData.brandingConfig?.brandColor || '#FF6B35',
        });

        setLoading(false);
      } catch (err) {
        console.error('Error fetching daycare info:', err);

        // If fetching fails, use fallback test values
        updateVideoData({
          daycareId: effectiveDaycareId,
          daycareName: 'Test Daycare',
          logoUrl: '',
          brandColor: '#FF6B35',
        });

        setLoading(false);
      }
    };

    ensureDaycareId();
  }, [daycareId, videoData.daycareId, updateVideoData]);

  // Handle post-payment redirect - jump to step 7
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && videoData.currentStep < 7) {
      // User just completed payment, jump to generate step
      updateVideoData({
        currentStep: 7,
        stripeSessionId: sessionId
      });
    }
  }, [searchParams, videoData.currentStep, updateVideoData]);

  const renderStep = () => {
    switch (videoData.currentStep) {
      case 1:
        return <Step1PetName />;
      case 2:
        return <Step2Photo />;
      case 3:
        return <Step3Emotion />;
      case 4:
        return <Step4Activities />;
      case 5:
        return <Step5Review />;
      case 6:
        return <Step6Payment />;
      case 7:
        return <Step7Generate />;
      default:
        return <Step1PetName />;
    }
  };

  const brandColor = videoData.brandColor || '#FF6B35';

  if (loading) {
    return (
      <div className="video-wizard loading" style={{ '--brand-color': brandColor }}>
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="video-wizard" style={{ '--brand-color': brandColor }}>
      {/* Progress Bar */}
      <div className="progress-bar">
        <div className="progress-steps">
          {[1, 2, 3, 4, 5, 6, 7].map((step) => (
            <div
              key={step}
              className={`progress-step ${
                step === videoData.currentStep ? 'active' : ''
              } ${step < videoData.currentStep ? 'completed' : ''}`}
            >
              <div className="progress-circle">
                {step < videoData.currentStep ? '✓' : step}
              </div>
              {step < 7 && <div className="progress-line"></div>}
            </div>
          ))}
        </div>
      </div>

      {/* Current Step */}
      {renderStep()}
    </div>
  );
};

export default VideoWizard;
